import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectors } from 'darwin_core';
import {
  splitTimeDuration,
  wifiSignalToBarCount,
  wifiSignalText,
} from 'darwin_core/utils/helpers.js';

import BackLink from '../navigation/BackLink.jsx';
import { alarmDetailPath, homePath } from '../navigation/paths.js';
import { ChevronRightIcon } from '../components/icons.jsx';
import { useHomeRouteContext } from '../navigation/HomeRoute.jsx';
import DataItem from '../data/DataItem.jsx';
import {
  formatOptimisationTime,
  formatServiceDue,
  signalToBars,
  weatherCompText,
} from '../data/dataModel.js';
import { alarmHeadText, alarmId, sameFaults } from '../data/alarmModel.js';
import useScreen from '../hooks/useScreen.js';

// Read-only Data screen — a single consolidated page (the app splits
// this across a Data menu + one sub-screen per category; a browser page shows the
// sections together). Everything here is a pure read of state already seeded into
// the store from the hello/homes payload — no new fetches and no writes. The
// heavier, separately-sourced parts of the app's Data feature (Energy via the
// diagnostics API, resolved-alarm history via the main REST API, Live Data over
// the socket) are deliberately out of this v1.
//
// Section gating mirrors app/src/screens/Property/Data/Menu.js: System status is
// always shown; a Logic device shows only that; every other section is for
// non-Logic devices, with the heat source and the per-zone/per-repeater sections
// keyed off the same selectors the app menu uses.

function Section({ title, children }) {
  // No aria-label: the <h2> names the section for heading navigation, and a home
  // renders many of these — turning each into a landmark region would clutter the
  // landmarks menu (the heading already provides the structure).
  return (
    <section className="data-section">
      <h2 className="data-section__title">{title}</h2>
      {children}
    </section>
  );
}

function SystemStatusSection({ homeId }) {
  const severity = useSelector((s) =>
    selectors.getSystemStatusSeverity(s, homeId),
  );
  // Rebuilt each call but with stable fault refs, so a shallow comparator keeps
  // useSelector from churning on unrelated store changes.
  const faults = useSelector(
    (s) => selectors.getAlarmsBySeverity(s, homeId),
    sameFaults,
  );

  return (
    <Section title="System status & history">
      <DataItem title="Status" value={severity || 'No problems'} />
      {faults.length > 0 && (
        <ul className="data-faults">
          {faults.map((fault, i) => {
            const severity = fault.severity
              ? String(fault.severity).toLowerCase()
              : 'information';
            return (
              <li
                key={`${fault.type ?? fault.faultCode ?? 'fault'}-${fault.raisedAt ?? i}`}
                className={`data-fault data-fault--${severity}`}
              >
                {/* Each fault deep-links to its own detail screen (resolution
                    guidance + contact numbers), reachable directly by URL. */}
                <Link
                  className="data-fault__link"
                  to={alarmDetailPath(homeId, alarmId(fault))}
                >
                  <span className="data-fault__head">
                    {alarmHeadText(fault)}
                  </span>
                  <span className="data-fault__desc">
                    {fault.description || fault.type || 'Fault'}
                  </span>
                  {fault.severity ? (
                    <span className="data-fault__meta">
                      <span className="data-fault__sev">{fault.severity}</span>
                    </span>
                  ) : null}
                  <span className="data-fault__chevron" aria-hidden="true">
                    <ChevronRightIcon />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function InterfaceSection({ homeId }) {
  const home = useSelector((s) => selectors.getHomeById(s, homeId));
  const failsafe = useSelector((s) => selectors.getFailsafeModeText(s, homeId));
  const isHaloAir = useSelector((s) => selectors.getIsHaloAir(s, homeId));

  const inter = home?.si || home?.hpi || {};
  const rssi = inter.wifiRssi;
  const bars =
    typeof rssi === 'number' ? wifiSignalToBarCount(rssi) : undefined;
  const signalText = bars === undefined ? undefined : wifiSignalText(bars);

  return (
    <Section title="Interface">
      <DataItem title="Device ID" value={homeId} />
      <DataItem title="Wi-Fi Network Name" value={inter.networkSsid} />
      <DataItem title="Wi-Fi Signal Strength" value={signalText} />
      {!isHaloAir && (
        <DataItem title="Weather Compensation" value={weatherCompText(home)} />
      )}
      <DataItem title="Failsafe mode" value={failsafe} />
      <DataItem
        title="Firmware Version"
        value={inter.firmwareVersionHpi || inter.firmwareVersionSi}
      />
    </Section>
  );
}

function HeatSourceSection({ homeId, isHaloAir }) {
  const home = useSelector((s) => selectors.getHomeById(s, homeId));

  if (isHaloAir) {
    return (
      <Section title="Heat pump">
        <DataItem title="Heat source model" value={home?.hpi?.modelName} />
        <DataItem
          title="Communication"
          value={home?.hpi?.connected ? 'OK' : 'Not OK'}
        />
        <DataItem
          title="Service due"
          value={formatServiceDue(
            home?.hpi?.serviceInterval,
            home?.hpi?.serviceTime,
          )}
        />
      </Section>
    );
  }

  return (
    <Section title="Boiler">
      <DataItem title="Boiler Model" value={home?.boiler?.boilerType} />
      <DataItem
        title="Communication"
        value={
          home?.boiler?.openThermCommunication ? 'Connected' : 'Disconnected'
        }
      />
    </Section>
  );
}

function ThermostatSection({ homeId, zoneId, isHaloAir }) {
  const zone = useSelector((s) => selectors.getZone(s, homeId, zoneId));
  const name = useSelector((s) =>
    selectors.getPrtFriendlyName(s, homeId, zoneId),
  );
  const battery = useSelector((s) =>
    selectors.getPrtBatteryLevel(s, homeId, zoneId),
  );
  const startMax = useSelector((s) =>
    selectors.getOptimisedStartMaxTime(s, homeId, zoneId),
  );
  const stopMax = useSelector((s) =>
    selectors.getOptimisedStopMaxTime(s, homeId, zoneId),
  );
  const startEnabled = useSelector((s) =>
    selectors.getoptimisedStartEnabled(s, homeId, zoneId),
  );
  const stopEnabled = useSelector((s) =>
    selectors.getoptimisedStopEnabled(s, homeId, zoneId),
  );
  const delayedEnabled = useSelector((s) =>
    selectors.getdelayedStartEnabled(s, homeId, zoneId),
  );
  const inHoliday = useSelector((s) =>
    selectors.isInHolidayMode(s, homeId, zoneId),
  );
  const childLock = useSelector((s) =>
    selectors.getChildLockEnabled(s, homeId, zoneId),
  );

  const signal = signalToBars(zone?.lastMessageRssi);
  const stopSplit = splitTimeDuration(stopMax);

  return (
    <Section title={`Thermostat — ${name || 'Zone'}`}>
      <DataItem title="Device Name" value={name} />
      <DataItem title="Signal Strength" value={signal.text} />
      <DataItem title="Battery Level" value={battery} />
      {isHaloAir ? (
        <>
          <DataItem
            title="Optimised Start Max Time"
            value={formatOptimisationTime(splitTimeDuration(startMax))}
          />
          <DataItem
            title="Optimised Stop Max Time"
            value={stopSplit.hours === 0 ? 'Off' : `${stopSplit.hours} hours`}
          />
        </>
      ) : (
        <>
          <DataItem
            title="Optimised Start"
            value={startEnabled === true ? 'Enabled' : 'Disabled'}
          />
          <DataItem
            title="Optimised Stop"
            value={stopEnabled === true ? 'Enabled' : 'Disabled'}
          />
          <DataItem
            title="Delayed Start"
            value={delayedEnabled === true ? 'Enabled' : 'Disabled'}
          />
        </>
      )}
      <DataItem
        title="Holiday Mode"
        value={inHoliday ? 'Active' : 'Inactive'}
      />
      <DataItem title="Child Lock" value={childLock ? 'Active' : 'Inactive'} />
      <DataItem title="Firmware Version" value={zone?.firmwareVersion} />
    </Section>
  );
}

function HotWaterSection({ homeId, zoneId, name }) {
  const timedDhw = useSelector((s) => selectors.getTimedDhw(s, homeId, zoneId));
  return (
    <Section title={`Hot Water — ${name || 'Zone'}`}>
      <DataItem
        title="Hot water schedule"
        value={timedDhw ? 'Active' : 'Inactive'}
      />
    </Section>
  );
}

function ZigbeeSection({ repeater }) {
  return (
    <Section title="Zigbee Booster">
      <DataItem
        title="Communication"
        value={repeater.connectionStatus ? 'Connected' : 'Disconnected'}
      />
      <DataItem title="Firmware" value={repeater.firmwareVersionRepeaterZ} />
    </Section>
  );
}

export default function DataScreen() {
  const headingRef = useScreen('Data');
  const { homeId } = useHomeRouteContext();
  const homeName = useSelector((s) => selectors.getHomeFriendlyName(s, homeId));
  const isLogic = useSelector((s) => selectors.getIsLogicDevice(s, homeId));
  const isHaloAir = useSelector((s) => selectors.getIsHaloAir(s, homeId));
  const zones = useSelector(
    (s) => selectors.getZones(s, homeId),
    shallowEqual,
  ).filter((zone) => Array.isArray(zone.zoneId));
  const repeaters = useSelector(
    (s) => selectors.getZigbeeRepeaters(s, homeId),
    shallowEqual,
  );

  return (
    <main className="app-shell screen data-screen">
      <header className="screen__header">
        <BackLink to={homePath(homeId)} label="Property" />
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          Data
        </h1>
        {homeName ? <p className="screen__sub">{homeName}</p> : null}
      </header>

      <SystemStatusSection homeId={homeId} />

      {!isLogic && (
        <>
          <InterfaceSection homeId={homeId} />
          <HeatSourceSection homeId={homeId} isHaloAir={isHaloAir} />
          {zones.map((zone) => {
            const key = zone.zoneId.join('_');
            if (zone.type === 'CH' || zone.type === 'HPI_CH') {
              return (
                <ThermostatSection
                  key={key}
                  homeId={homeId}
                  zoneId={zone.zoneId}
                  isHaloAir={isHaloAir}
                />
              );
            }
            if (zone.type === 'HPI_DHW') {
              return (
                <HotWaterSection
                  key={key}
                  homeId={homeId}
                  zoneId={zone.zoneId}
                  name={zone.friendlyName}
                />
              );
            }
            return null;
          })}
          {repeaters.map((repeater) => (
            <ZigbeeSection key={repeater.deviceId} repeater={repeater} />
          ))}
        </>
      )}
    </main>
  );
}
